/** Formatting helpers — Indian locale (₹, lakh/crore grouping) by default. */
const LOCALE = 'en-IN';

const currencyFmt = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
const numberFmt = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 2 });
const dateFmt = new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: 'short', year: 'numeric' });
const dateTimeFmt = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});
const monthFmt = new Intl.DateTimeFormat(LOCALE, { month: 'short', year: '2-digit' });

export function formatCurrency(value: number | null | undefined, currency = 'INR'): string {
  if (value == null || Number.isNaN(value)) return '—';
  if (currency !== 'INR') {
    return new Intl.NumberFormat(LOCALE, { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
  }
  return currencyFmt.format(value);
}

/** Compact Indian notation: ₹4.2 Cr, ₹38.5 L, ₹12.0 K. */
export function formatCurrencyCompact(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  const abs = Math.abs(value);
  const sign = value < 0 ? '−' : '';
  if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(abs >= 1e9 ? 0 : 2)} Cr`;
  if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(1)} L`;
  if (abs >= 1e3) return `${sign}₹${(abs / 1e3).toFixed(1)} K`;
  return `${sign}₹${abs.toFixed(0)}`;
}

export function formatNumber(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return numberFmt.format(value);
}

export function formatCompactNumber(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  const abs = Math.abs(value);
  if (abs >= 1e7) return `${(value / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `${(value / 1e5).toFixed(1)} L`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(1)} K`;
  return numberFmt.format(value);
}

export function formatPercent(value: number | null | undefined, digits = 0): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${value.toFixed(digits)}%`;
}

function toDate(value: string | Date): Date {
  if (value instanceof Date) return value;
  // Treat bare dates as local dates (avoid UTC shift).
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = toDate(value);
  return Number.isNaN(d.getTime()) ? '—' : dateFmt.format(d);
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = toDate(value);
  return Number.isNaN(d.getTime()) ? '—' : dateTimeFmt.format(d);
}

/** 'YYYY-MM' → "Jan 26" */
export function formatMonthKey(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return monthFmt.format(new Date(y, (m ?? 1) - 1, 1));
}

export function formatRelative(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = toDate(value);
  const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
  const abs = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' });
  if (abs < 60) return rtf.format(-diffSec, 'second');
  if (abs < 3600) return rtf.format(-Math.round(diffSec / 60), 'minute');
  if (abs < 86400) return rtf.format(-Math.round(diffSec / 3600), 'hour');
  if (abs < 86400 * 30) return rtf.format(-Math.round(diffSec / 86400), 'day');
  return formatDate(d);
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
}

/** snake_case enum → "Title Case" */
export function humanize(value: string | null | undefined): string {
  if (!value) return '—';
  return value
    .split('_')
    .map((w) => (w.length <= 2 && w !== 'ra' ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1)))
    .join(' ')
    .replace(/\bRa\b/, 'RA');
}

/**
 * Quantity with its unit. Line-item tenders track their overall quantity as value in ₹
 * (unit "INR"), so those are shown as currency.
 */
export function formatQty(value: number | null | undefined, unit: string | null | undefined): string {
  if (unit === 'INR') return formatCurrency(value);
  return `${formatNumber(value)}${unit ? ` ${unit}` : ''}`;
}

const rateFmt = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 2 });
/** ₹ amount keeping paise when present (unit rates such as ₹2.5, line item amounts). */
export function formatRate(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return rateFmt.format(value);
}
